import { Box, styled } from "@mui/material";
import { Search, Tabs } from "@procore/core-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CORE_PROPERTIES_TAB_IDS, usePropertiesTabs } from "contexts/propertiesTabs";
import { SidePanelProps } from "contexts/sidePanels";
import { useObjectData } from "hooks/useObjectData";
import { selectIsOnline } from "slices/explorer";

const PanelContent = styled(Box)`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const TabsSection = styled(Box)`
    padding: 0 16px;
    border-bottom: 1px solid ${TOOLBAR_COLORS.BORDER};
    flex-shrink: 0;
`;

const SearchSection = styled(Box)`
    padding: 8px 16px;
    border-bottom: 1px solid ${TOOLBAR_COLORS.BORDER};
    flex-shrink: 0;
`;

const ContentSection = styled(Box)`
    flex: 1;
    min-height: 0;
    overflow: auto;
`;

/**
 * Properties panel - integrated PropertiesWindow content
 */
export function PropertiesPanel(_props: SidePanelProps) {
    const { object, hasAccess, isLoading } = useObjectData();
    const customTabs = usePropertiesTabs();
    const [searchTerm, setSearchTerm] = useState("");
    const { t } = useTranslation();
    const isOnline = useAppSelector(selectIsOnline);
    const {
        state: { view },
    } = useExplorerGlobals();
    const propertiesUnavailableOffline = !isOnline && !view?.data;

    const [activeTab, setActiveTab] = useState<string>(() => {
        return Object.keys(customTabs.tabEntries)[0] || CORE_PROPERTIES_TAB_IDS.PROPERTIES;
    });

    const [searchEmptyState, setSearchEmptyState] = useState(false);
    const isSearchActive = searchTerm.trim().length > 0;

    const error = object && !hasAccess ? t("objectAccessForbidden") : null;

    const renderActiveTab = () => {
        const tabEntry = customTabs.tabEntries[activeTab];
        if (!tabEntry) {
            return <div data-testid="properties-tab-not-found">Tab not found</div>;
        }

        const TabComponent = tabEntry.config.component;

        return (
            <div data-testid={`properties-tab-content-${activeTab}`}>
                <TabComponent
                    object={object}
                    hasAccess={hasAccess}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    onEmptyStateChange={setSearchEmptyState}
                />
            </div>
        );
    };

    const allTabs = Object.values(customTabs.tabEntries).map((tabEntry) => ({
        id: tabEntry.config.id,
        label: tabEntry.config.id === CORE_PROPERTIES_TAB_IDS.PROPERTIES ? t("properties") : tabEntry.config.label,
    }));

    return (
        <PanelContent>
            <TabsSection>
                <Tabs data-testid="properties-tabs">
                    {allTabs.map((tab) => (
                        <Tabs.Tab
                            key={tab.id}
                            selected={activeTab === tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            data-testid={`properties-tab-${tab.id}`}
                        >
                            {tab.label}
                        </Tabs.Tab>
                    ))}
                </Tabs>
            </TabsSection>

            {activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && (
                <SearchSection>
                    <Search
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t("searchProperties")}
                        autoComplete="off"
                    />
                </SearchSection>
            )}

            {isLoading && (
                <Box sx={{ padding: 2, textAlign: "center", color: "text.secondary" }}>{t("loadingProperties")}</Box>
            )}

            {error && (
                <Box sx={{ padding: 2, textAlign: "center" }}>
                    <Box sx={{ color: "error.main", fontWeight: 600 }}>{error}</Box>
                </Box>
            )}

            {!object && !isLoading && propertiesUnavailableOffline && (
                <Box sx={{ padding: 2, textAlign: "center" }}>
                    <Box sx={{ color: "text.primary", fontWeight: 600 }}>{t("propertiesUnavailableOffline")}</Box>
                    <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1 }}>
                        {t("propertiesUnavailableOfflineMessage")}
                    </Box>
                </Box>
            )}

            {!object && !isLoading && !propertiesUnavailableOffline && (
                <Box sx={{ padding: 2, textAlign: "center" }}>
                    <Box sx={{ color: "text.primary", fontWeight: 600 }}>{t("selectAnObject")}</Box>
                    <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1 }}>
                        {t("selectObjectForProperties")}
                    </Box>
                </Box>
            )}

            {activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && isSearchActive && searchEmptyState && !isLoading && (
                <Box sx={{ padding: 2, textAlign: "center" }}>
                    <Box sx={{ color: "text.primary", fontWeight: 600 }}>{t("noPropertiesMatchSearch")}</Box>
                    <Box sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1 }}>{t("pleaseRetrySearch")}</Box>
                </Box>
            )}

            {!isLoading &&
                !error &&
                object &&
                !(activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && isSearchActive && searchEmptyState) && (
                    <ContentSection>{renderActiveTab()}</ContentSection>
                )}
        </PanelContent>
    );
}
